import React from 'react';
import classNames from 'classnames';
import Avatar from '@atlaskit/avatar';
import Tooltip from '@atlaskit/tooltip';
import { InvoiceGroup, PaymentMethodTypeV2 } from '@css-xp/graphtypes';
import { EndCustomerContact } from '@css-xp/graphtypes/src/generated-types-new';
import { SkeletonLoader } from '@css-xp/skeleton-loader';
import { SkeletonLoaderWrapper } from '@css-xp/skeleton-loader/src/skeleton-loader';
import { sentenceCase } from '@atlassian/cssxp-utils-generic';
import { Box, Flex, Text, TextColor } from '@atlaskit/primitives';
import { PaymentMethodId, PaymentMethodPanel } from '@atlassian/commerce-payment-methods';
import { COMMERCIAL_PRICE_ELIGIBILITY } from '@css-xp/ppc-constants';
import { isAchPaymentMethod } from '@css-xp/ppc-utils';
import { PaypalPaymentMethod } from '@css-xp/ppc-paypal';
import { BillingProfileSelectItem } from './billing-profile-form-field';
import { BillingProfileCardContainer, getBoxStyles } from './switch-billing-profile-cards.styles';
import { formatAddressLabel } from './select-ship-to-address';
import { AchPanel } from '../../ach/src/ach-panel';

interface Props {
  billingProfiles: BillingProfileSelectItem[];
  setSelectedInvoiceGroupPricingPlanValue?: Function | null;
  onChange?: (selectedItem: BillingProfileSelectItem) => void;
  selected?: InvoiceGroup | null;
  primaryCustomerContact?: {
    isLoading?: boolean;
    data?: EndCustomerContact;
  };
  usePrimaryContactFromBillingProfile?: boolean;
  loadingBillingProfiles?: boolean;
  shouldShowACHPaymentMethod?: boolean;
}

export const SwitchBillingProfileCards = ({
  billingProfiles,
  setSelectedInvoiceGroupPricingPlanValue,
  selected,
  onChange,
  primaryCustomerContact,
  usePrimaryContactFromBillingProfile = false,
  loadingBillingProfiles = false,
  shouldShowACHPaymentMethod = false,
}: Props) => {
  if (usePrimaryContactFromBillingProfile && loadingBillingProfiles) {
    return (
      <SkeletonLoaderWrapper>
        {Array.from({ length: 6 }).map((_, index) => (
          <SkeletonLoaderWrapper key={index}>
            <SkeletonLoader width="100%" height="150px" marginBottom="16px" borderRadius="8px" />
          </SkeletonLoaderWrapper>
        ))}
      </SkeletonLoaderWrapper>
    );
  }

  const renderPaymentMethod = (billingProfile: InvoiceGroup, isTextDisabled?: boolean) => {
    const defaultPaymentMethod = billingProfile?.defaultPaymentMethod as any;

    if (isAchPaymentMethod(defaultPaymentMethod?.type)) {
      return (
        <AchPanel
          bankName={defaultPaymentMethod?.ach?.bankName}
          last4={defaultPaymentMethod?.ach?.last4}
          iconHeight={24}
          iconWidth={48}
        />
      );
    }

    if (defaultPaymentMethod?.type === PaymentMethodTypeV2.PAYPAL) {
      return (
        <PaypalPaymentMethod
          emailID={defaultPaymentMethod?.payPal?.email}
          renderChildrenInline
          withBorder={false}
          isDesignIteration1Enabled
          iconHeight={24}
          iconWidth={48}
        />
      );
    }

    return (
      <Flex>
        <div
          className={classNames('payment-method-panel-one-column-layout', {
            'payment-method-panel-one-column-layout-disabled ': isTextDisabled,
          })}
        >
          <PaymentMethodPanel
            paymentMethod={{
              id: defaultPaymentMethod.id as PaymentMethodId,
              type: defaultPaymentMethod.type,
              card: {
                brand: defaultPaymentMethod.card?.brand,
                expMonth: defaultPaymentMethod.card?.expirationMonth,
                expYear: defaultPaymentMethod.card?.expirationYear,
                last4: defaultPaymentMethod.card?.last4Numbers,
              },
              default: defaultPaymentMethod?.default ?? false,
            }}
          />
        </div>
      </Flex>
    );
  };

  return (
    <Flex gap="space.200" direction="column">
      {billingProfiles.map((invoiceGroupItem: BillingProfileSelectItem) => {
        const {
          name,
          currency,
          shipToPriceEligibility,
          shipToParty,
          id,
          defaultPaymentMethod,
        } = invoiceGroupItem.invoiceGroup;
        const disabledMessage = invoiceGroupItem?.disabledMessage;
        const isTextDisabled = !!disabledMessage;
        const getTextColor = (color = 'color.text') => (isTextDisabled ? 'color.text.disabled' : color) as TextColor;

        const handleBillingProfileClick = () => {
          if (disabledMessage || selected?.id === id) {
            return;
          }

          if (setSelectedInvoiceGroupPricingPlanValue) {
            setSelectedInvoiceGroupPricingPlanValue(shipToPriceEligibility ?? '');
          }

          if (onChange) {
            onChange(invoiceGroupItem);
          }
        };

        const renderCustomerContactPicture = () => {
          const primaryContact = usePrimaryContactFromBillingProfile
            ? invoiceGroupItem.primaryCustomerContact
            : primaryCustomerContact;

          if (usePrimaryContactFromBillingProfile || selected?.id === id) {
            if (primaryContact?.isLoading) {
              return (
                <SkeletonLoaderWrapper>
                  <SkeletonLoader circle={true} width="25%" height="25px" />
                </SkeletonLoaderWrapper>
              );
            }

            const renderTooltipContent = () => {
              if (!primaryContact?.data) {
                return <>Unassigned (Primary)</>;
              }

              return (
                <>
                  {primaryContact?.data?.name} (Primary) <br /> {primaryContact?.data?.email}
                </>
              );
            };

            return (
              <Tooltip content={renderTooltipContent()}>
                <Avatar src={primaryContact?.data?.picture ?? ''} size="small" />
              </Tooltip>
            );
          }
        };

        if (!shouldShowACHPaymentMethod && isAchPaymentMethod(defaultPaymentMethod?.type)) return null;

        return (
          <BillingProfileCardContainer key={id} isTextDisabled={isTextDisabled}>
            <Box xcss={getBoxStyles(selected, id, isTextDisabled)} onClick={handleBillingProfileClick}>
              <Flex justifyContent="space-between">
                <Flex direction="column" gap="space.025">
                  <span className="text-ellipsis">
                    <Text weight="semibold" color={getTextColor()}>
                      {name}
                    </Text>
                  </span>
                  <Text color={getTextColor('color.text.subtle')} size="small">
                    {sentenceCase(shipToPriceEligibility ?? COMMERCIAL_PRICE_ELIGIBILITY)} | {currency}
                  </Text>
                </Flex>
                {renderCustomerContactPicture()}
              </Flex>
              {defaultPaymentMethod ? (
                renderPaymentMethod(invoiceGroupItem?.invoiceGroup, isTextDisabled)
              ) : (
                <Text color={getTextColor()}>No payment method</Text>
              )}
              {shipToParty && (
                <Text color={getTextColor()} size="small">
                  {formatAddressLabel(shipToParty)}
                </Text>
              )}
              {isTextDisabled && (
                <Text color="color.text.danger" size="small">
                  {disabledMessage}
                </Text>
              )}
            </Box>
          </BillingProfileCardContainer>
        );
      })}
    </Flex>
  );
};
